import { 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  orderBy 
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Occasion } from '../types';
import { paymentsService } from './paymentsService';

const OCCASIONS_COLLECTION = 'Occasions';

export const occasionsService = {
  // Get all occasions
  async getAllOccasions(): Promise<Occasion[]> {
    try {
      const occasionsRef = collection(db, OCCASIONS_COLLECTION);
      // Try to order by occasionDate, but if it fails, get all docs without ordering
      let querySnapshot;
      try {
        const q = query(occasionsRef, orderBy('occasionDate', 'desc'));
        querySnapshot = await getDocs(q);
      } catch (orderError) {
        querySnapshot = await getDocs(occasionsRef);
      }
      
      const occasions = await Promise.all(querySnapshot.docs.map(async (doc) => {
        const data = doc.data();
        
        
        // Get payment count from subcollection
        let paymentCount = 0;
        try {
          const payments = await paymentsService.getPaymentsByOccasionId(doc.id);
          paymentCount = payments.length;
        } catch (error) {
          console.log(`Error fetching payments for occasion ${doc.id}:`, error);
          // Fallback to checking for payments array in document
          if (data.payments && Array.isArray(data.payments)) {
            paymentCount = data.payments.length;
          } else if (data.payment && Array.isArray(data.payment)) {
            paymentCount = data.payment.length;
          } else if (data.paymentList && Array.isArray(data.paymentList)) {
            paymentCount = data.paymentList.length;
          } else {
            console.log('No payment arrays found in this occasion');
          }
        }
        
        return {
          occasionId: doc.id,
          ...data,
          paymentCount // Add payment count to the occasion data
        } as Occasion & { paymentCount: number };
      }));
      
      return occasions;
    } catch (error) {
      console.error('Error fetching occasions:', error);
      throw error;
    }
  },

  // Get occasion by ID
  async getOccasionById(id: string): Promise<Occasion | null> {
    try {
      const occasionsRef = collection(db, OCCASIONS_COLLECTION);
      const querySnapshot = await getDocs(occasionsRef);
      
      const occasionDoc = querySnapshot.docs.find(doc => doc.id === id);
      if (occasionDoc) {
        return {
          occasionId: occasionDoc.id,
          ...occasionDoc.data()
        } as Occasion;
      }
      return null;
    } catch (error) {
      console.error('Error fetching occasion:', error);
      throw error;
    }
  },

  // Create new occasion
  async createOccasion(occasionData: Omit<Occasion, 'occasionId'>): Promise<string> {
    try {
      const occasionsRef = collection(db, OCCASIONS_COLLECTION);
      const docRef = await addDoc(occasionsRef, occasionData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating occasion:', error);
      throw error;
    }
  },

  // Update occasion
  async updateOccasion(id: string, occasionData: Partial<Occasion>): Promise<void> {
    try {
      const occasionRef = doc(db, OCCASIONS_COLLECTION, id);
      await updateDoc(occasionRef, occasionData);
    } catch (error) {
      console.error('Error updating occasion:', error);
      throw error;
    }
  },

  // Delete occasion
  async deleteOccasion(id: string): Promise<void> {
    try {
      const occasionRef = doc(db, OCCASIONS_COLLECTION, id);
      await deleteDoc(occasionRef);
    } catch (error) {
      console.error('Error deleting occasion:', error);
      throw error;
    }
  }
};
